import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  Layers,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  FEED_ACTION_LABEL,
  FEED_STATE_ORDER,
  FEED_ACTION_TYPES,
  SEVERITY_RANK,
  rowMatchesFilters,
  rowMatchesQuery,
  threatActionType,
  type FeedActionType,
  type FeedRow,
  type FeedStateKind,
  type FeedThreat,
  type Severity,
  type ThreatRowView,
} from '../data/selectors/feed';
import { EntityChips, SEVERITY_CLASS, SourceNames, StateBadge } from './c3/FeedBits';
import { useDataClock } from '../data';
import { useFeedData } from '../data/queries/feedData';
import { useQueryList, useQueryParam } from '../app/useQueryParam';
import type { PeriodWindow } from '../lib/funnel';
import { campaignLabel, incidentLabel } from '../lib/ids';
import { sourceLabel, sourcesLine } from '../lib/sourceNames';
import { formatAge, formatUtcDateTime, formatUtcTime } from '../lib/time';
import type { Needs } from '../lib/needs';
import {
  FEED_STATE_CHOICES,
  FEED_TYPE_CHOICES,
  type FeedStateGroup,
  type FeedType,
} from '../lib/stageDetail';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import FeedFilterControls, { SEVERITY_CHOICES, TIME_CHOICES } from './c3/FeedFilterControls';
import Skeleton from '../ui/Skeleton';

type FeedView = 'active' | 'suppressed';

const FEED_VIEWS: readonly FeedView[] = ['active', 'suppressed'];
/** A feed row is one line: the first two entities and a count of the rest (their names are on the count's tooltip). */
const FEED_ENTITY_LIMIT = 2;
type SortKey =
  | 'default'
  | 'severity'
  | 'incident'
  | 'updated'
  | 'sources'
  | 'entities'
  | 'state';
type SortDir = 'asc' | 'desc';

export type FeedGraphScope = {
  type: 'campaign' | 'threat';
  id: string;
  label: string;
};

interface ThreatFeedProps {
  /** Opens a campaign's screen: the campaign's id, or a lone incident's (a campaign of one). Only campaigns have it. */
  onOpenCampaign?: (id: string) => void;
  /** Told what waits on a person each time the feed loads, and null when the feed leaves the page (item 45). */
  onNeeds?: (needs: Needs | null) => void;
  onViewInGraph?: (scope: FeedGraphScope) => void;
  /** Scopes the Threat Index to an incident or a campaign, as View in Graph scopes the graph. */
  onViewInIndex?: (scope: FeedGraphScope) => void;
}

/** Sorting by State goes from the queue to the end, as the state filter lists them. */
const STATE_RANK: Record<FeedStateKind, number> = Object.fromEntries(
  FEED_STATE_ORDER.map((kind, index) => [kind, index])
) as Record<FeedStateKind, number>;

/** How long a new arrival is tinted, so an update nobody asked for can be seen (client notes 10/26, item 20). */
const FRESH_MS = 8000;

const DEFAULT_SORT_DIR: Record<Exclude<SortKey, 'default'>, SortDir> = {
  severity: 'desc',
  updated: 'desc',
  incident: 'asc',
  sources: 'asc',
  entities: 'desc',
  state: 'asc',
};

function cmpNum(a: number, b: number): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/** Default: severity↓, last updated newest-first, campaign before incident. */
function compareDefault(a: FeedRow, b: FeedRow): number {
  const sev = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
  if (sev) return sev;
  const updatedA = a.lastUpdatedMs ?? Number.NEGATIVE_INFINITY;
  const updatedB = b.lastUpdatedMs ?? Number.NEGATIVE_INFINITY;
  if (updatedA !== updatedB) return updatedA < updatedB ? 1 : -1;
  if (a.kind !== b.kind) return a.kind === 'campaign' ? -1 : 1;
  return a.title.localeCompare(b.title);
}

function compareByKey(a: FeedRow, b: FeedRow, key: Exclude<SortKey, 'default'>): number {
  switch (key) {
    case 'severity':
      return SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    case 'updated':
      return cmpNum(a.lastUpdatedMs ?? Number.NEGATIVE_INFINITY, b.lastUpdatedMs ?? Number.NEGATIVE_INFINITY);
    case 'incident': {
      if (a.kind !== b.kind) return a.kind === 'campaign' ? -1 : 1;
      return a.title.localeCompare(b.title);
    }
    case 'sources':
      return sourcesLine(a.sources).localeCompare(sourcesLine(b.sources));
    case 'entities':
      return a.entities.length - b.entities.length;
    case 'state':
      return STATE_RANK[a.state.kind] - STATE_RANK[b.state.kind];
    default:
      return 0;
  }
}

function SortHead({
  label,
  hint,
  col,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  /** What the column holds, when its name alone does not say it. */
  hint?: string;
  col: Exclude<SortKey, 'default'>;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (col: Exclude<SortKey, 'default'>) => void;
}) {
  const active = sortKey === col;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      className={`tf-sort-head${active ? ' is-active' : ''}`}
      onClick={() => onSort(col)}
      title={`${hint ? `${hint} · ` : ''}${active ? `Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`}`}
    >
      <span>{label}</span>
      <Icon className="tf-sort-icon" aria-hidden />
    </button>
  );
}

const ThreatFeed: React.FC<ThreatFeedProps> = ({
  onOpenCampaign,
  onNeeds,
  onViewInGraph,
  onViewInIndex,
}) => {
  const [view, setView] = useQueryParam<FeedView>('view', 'active', FEED_VIEWS);
  // Every filter keeps what is ticked in the address as a comma-separated list (client notes 10/26, item 14); none ticked is
  // no filter. A pipeline stage opens the feed on a state or a type (stage detail, docs/c3/DESIGN.md): a list of one.
  const [sourceFilter, setSourceFilter] = useQueryList<string>('source');
  const [severityFilter, setSeverityFilter] = useQueryList<Severity>('severity', SEVERITY_CHOICES);
  const [stateFilter, setStateFilter] = useQueryList<FeedStateGroup>('state', FEED_STATE_CHOICES);
  const [typeFilter, setTypeFilter] = useQueryList<FeedType>('type', FEED_TYPE_CHOICES);
  const [actionFilter, setActionFilter] = useQueryList<FeedActionType>('action', FEED_ACTION_TYPES);
  const [timeFilter, setTimeFilter] = useQueryParam<PeriodWindow | 'all'>('time', 'all', TIME_CHOICES);
  // Search finds only what the feed lists (client notes 10/26, items 2 and 3). What is typed shows at once; the address, and
  // so the rows, follow when the typing pauses. `written` is the last text this field put in the address, so an address
  // that arrives a render late does not overwrite what was typed since, while one changed from elsewhere (Clear filters,
  // a pipeline stage's link) does reach the field.
  const [query, setQuery] = useQueryParam<string>('q', '');
  const [draft, setDraft] = useState(query);
  const written = useRef(query);
  useEffect(() => {
    if (query === written.current) return;
    written.current = query;
    setDraft(query);
  }, [query]);
  useEffect(() => {
    if (draft === written.current) return;
    const pause = setTimeout(() => {
      written.current = draft;
      setQuery(draft);
    }, 250);
    return () => clearTimeout(pause);
  }, [draft, setQuery]);
  // The records come from the shared cache and the one refresh clock (docs/c3/decisions/002-data-layer.md).
  const { rows, suppressed, loading, error, refresh } = useFeedData();
  const clock = useDataClock();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [sortKey, setSortKey] = useState<SortKey>('default');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => {
    const tick = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(tick);
  }, []);

  // The clock reads the feed's records again every few seconds; what arrives after the first load is tinted for a moment.
  const known = useRef<Set<string> | null>(null);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());
  const freshTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => {
    if (loading) return;
    const seen = known.current;
    const ids = rows.map((row) => row.id);
    if (seen == null) {
      known.current = new Set(ids);
      return;
    }
    const added = ids.filter((id) => !seen.has(id));
    if (added.length === 0) return;
    added.forEach((id) => seen.add(id));
    setFresh((prev) => new Set(Array.from(prev).concat(added)));
    freshTimers.current.push(
      setTimeout(
        () =>
          setFresh((prev) => {
            const next = new Set(prev);
            added.forEach((id) => next.delete(id));
            return next;
          }),
        FRESH_MS
      )
    );
  }, [rows, loading]);
  useEffect(() => {
    const timers = freshTimers.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) {
      for (const source of row.sources) {
        if (source) set.add(source);
      }
    }
    return Array.from(set).sort();
  }, [rows]);

  // The clock moves the rows in and out of a time filter; with none, it has no say, so the 5 s tick does not sort again.
  const filterNow = timeFilter === 'all' ? 0 : nowMs;
  const visibleRows = useMemo(() => {
    const filters = {
      sources: sourceFilter,
      severities: severityFilter,
      states: stateFilter,
      types: typeFilter,
      actions: actionFilter,
      period: timeFilter === 'all' ? null : timeFilter,
    };
    const filtered = rows.filter((row) => rowMatchesFilters(row, filters, filterNow) && rowMatchesQuery(row, query));
    const sorted = [...filtered];
    if (sortKey === 'default') {
      sorted.sort(compareDefault);
    } else {
      sorted.sort((a, b) => {
        const base = compareByKey(a, b, sortKey);
        return sortDir === 'asc' ? base : -base;
      });
    }
    return sorted;
  }, [rows, sourceFilter, severityFilter, typeFilter, stateFilter, actionFilter, timeFilter, filterNow, query, sortKey, sortDir]);

  const filtering =
    sourceFilter.length > 0 ||
    severityFilter.length > 0 ||
    stateFilter.length > 0 ||
    typeFilter.length > 0 ||
    actionFilter.length > 0 ||
    timeFilter !== 'all' ||
    query.trim() !== '';
  const clearFilters = () => {
    setSourceFilter([]);
    setSeverityFilter([]);
    setStateFilter([]);
    setTypeFilter([]);
    setActionFilter([]);
    setTimeFilter('all');
    written.current = '';
    setDraft('');
    setQuery('');
  };

  const onSortColumn = (col: Exclude<SortKey, 'default'>) => {
    if (sortKey === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(col);
    setSortDir(DEFAULT_SORT_DIR[col]);
  };

  const toggleExpand = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // What waits on a person, from the rows already loaded. `decideNext` reads the latest handler through a ref, so
  // the summary changes only when the rows do and the shell, which stores it, does not render in a loop.
  const openRef = useRef(onOpenCampaign);
  openRef.current = onOpenCampaign;
  const waiting = useMemo(() => {
    let count = 0;
    let oldest: ThreatRowView | null = null;
    let oldestRowId = '';
    for (const row of rows) {
      for (const member of row.members) {
        if (member.state.kind !== 'pending_approval') continue;
        count += 1;
        if (!oldest || (member.firstEventMs ?? Infinity) < (oldest.firstEventMs ?? Infinity)) {
          oldest = member;
          oldestRowId = row.id;
        }
      }
    }
    return { count, oldest, oldestRowId };
  }, [rows]);
  // `decideNext` reads the oldest through a ref, so it is the same function while the rows are polled, and the
  // summary is published only when the count or the oldest time changes: the 30 s poll does not re-render the shell.
  const oldestRef = useRef('');
  oldestRef.current = waiting.oldestRowId;
  const decideNext = useCallback(() => {
    const rowId = oldestRef.current;
    if (rowId) openRef.current?.(rowId);
  }, []);
  const waitingCount = waiting.count;
  const oldestMs = waiting.oldest?.firstEventMs ?? null;
  useEffect(() => {
    onNeeds?.(waitingCount > 0 ? { count: waitingCount, oldestMs, decideNext } : null);
  }, [waitingCount, oldestMs, decideNext, onNeeds]);
  useEffect(() => () => onNeeds?.(null), [onNeeds]);

  const renderEntityChips = (entities: Parameters<typeof EntityChips>[0]['entities']) => (
    <EntityChips entities={entities} limit={FEED_ENTITY_LIMIT} />
  );

  const renderState = (state: ThreatRowView['state']) => <StateBadge state={state} />;

  // How long ago something last happened to it; the exact time is on the tooltip.
  const renderUpdated = (ms: number | null) =>
    ms == null ? (
      <span className="tf-empty">—</span>
    ) : (
      <span title={formatUtcDateTime(ms)}>{formatAge(ms, nowMs) === '—' ? 'just now' : `${formatAge(ms, nowMs)} ago`}</span>
    );

  /** What the graph, or the Threat Index, is scoped to for an incident: its action when it has one, else the event. */
  const scopeOfThreat = (threat: FeedThreat): FeedGraphScope | null => {
    const scopeId = threat.action?._id || threat.event._id;
    return scopeId ? { type: 'threat', id: scopeId, label: incidentLabel(threat.event._id || scopeId) } : null;
  };

  /** The same for a row: a campaign's own id, or the incident of a row that stands alone. */
  const scopeOfRow = (row: FeedRow): FeedGraphScope | null => {
    if (row.kind === 'campaign') {
      const campaignId = row.campaign?._id || row.id;
      return campaignId ? { type: 'campaign', id: campaignId, label: row.title || campaignLabel(campaignId) } : null;
    }
    const threat = row.members[0]?.threat || row.threats[0];
    return threat ? scopeOfThreat(threat) : null;
  };

  /**
   * What can be done with an open incident or campaign (client notes 10/26, items 18 and 19): Analyze and Remediate, which
   * opens its screen, and View in Graph and View in Index, which scope the Workspace to it. A button is there only when the
   * screen can act on it. `id` is what the screen opens: a campaign's, or an incident's (which opens its own campaign).
   */
  const renderActions = (id: string, scope: FeedGraphScope | null) => (
    <div className="tf-detail-actions">
      {onOpenCampaign ? (
        <button type="button" className="c3-btn c3-btn-primary c3-btn-sm" onClick={() => onOpenCampaign(id)}>
          Analyze and Remediate
        </button>
      ) : null}
      {scope && onViewInGraph ? (
        <button type="button" className="c3-btn c3-btn-sm" onClick={() => onViewInGraph(scope)}>
          View in Graph
        </button>
      ) : null}
      {scope && onViewInIndex ? (
        <button type="button" className="c3-btn c3-btn-sm" onClick={() => onViewInIndex(scope)}>
          View in Index
        </button>
      ) : null}
    </div>
  );

  const actionTypesOf = (members: ThreatRowView[]): FeedActionType[] =>
    FEED_ACTION_TYPES.filter((type) => members.some((member) => threatActionType(member.threat) === type));

  /**
   * What an open incident or campaign says about itself, in a panel under its row, with its buttons. A campaign's sits at
   * level 1, with its incidents under it; an incident's sits at level 1 when it stands alone and at level 2 inside a
   * campaign, indented once more (client notes 10/26, item 17): the panel is the same, only how far it is pushed in changes.
   */
  const renderDetail = (
    subject: Pick<ThreatRowView, 'description' | 'sources' | 'entities' | 'firstEventMs'>,
    members: ThreatRowView[],
    label: string,
    actions: React.ReactNode,
    level: 1 | 2
  ) => (
    <div className={`tf-detail is-level-${level}`} role="group" aria-label={`Details of ${label}`}>
      <dl className="tf-detail-facts">
        <div className="is-wide">
          <dt>Description</dt>
          <dd>{subject.description || <span className="tf-empty">No description</span>}</dd>
        </div>
        <div>
          <dt>Received</dt>
          <dd>{subject.firstEventMs == null ? '—' : formatUtcDateTime(subject.firstEventMs)}</dd>
        </div>
        <div>
          <dt>Action type</dt>
          <dd>{actionTypesOf(members).map((type) => FEED_ACTION_LABEL[type]).join(' · ')}</dd>
        </div>
        <div>
          <dt>Sources</dt>
          <dd>
            <SourceNames sources={subject.sources} />
          </dd>
        </div>
        <div className="is-wide">
          <dt>Affected entities</dt>
          <dd>
            <EntityChips entities={subject.entities} limit={subject.entities.length} />
          </dd>
        </div>
      </dl>
      {actions}
    </div>
  );

  const renderIncidentCell = (row: FeedRow, open: boolean) => {
    const idLabel =
      row.kind === 'campaign' ? campaignLabel(row.campaign?._id || row.id) : incidentLabel(row.id);
    const Chevron = open ? ChevronDown : ChevronRight;
    const isNewThreat =
      row.kind === 'standalone' &&
      row.firstEventMs != null &&
      nowMs - row.firstEventMs < 60 * 60 * 1000 &&
      nowMs >= row.firstEventMs;
    return (
      <div className="tf-incident">
        <div className="tf-incident-top">
          <button
            type="button"
            className="tf-chevron"
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${row.title}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(row.id);
            }}
          >
            <Chevron className="w-3.5 h-3.5" aria-hidden />
          </button>
          <div className="tf-incident-copy">
            <div className="tf-incident-title-row">
              <span className="tf-incident-title" title={row.title}>{row.title}</span>
              {isNewThreat ? <span className="tf-new-badge">new</span> : null}
              {row.kind === 'campaign' ? (
                <span className="tf-incident-count">
                  <Layers className="w-3 h-3" aria-hidden />
                  {row.threats.length} incident{row.threats.length === 1 ? '' : 's'}
                </span>
              ) : null}
            </div>
            <div className="tf-incident-meta">
              <span
                className={`tf-incident-id ${
                  row.kind === 'campaign' ? 'is-campaign' : 'is-threat'
                }`}
              >
                {idLabel}
              </span>
              {row.description ? (
                <span className="tf-incident-desc" title={row.description}>{row.description}</span>
              ) : (
                <span className="tf-incident-desc is-empty">No description</span>
              )}
            </div>
          </div>
          {renderOpenBtn(row)}
        </div>
      </div>
    );
  };

  // On every top-level row: a campaign, or an incident that stands alone, which is a campaign of one. Not on the
  // incidents inside a campaign (renderMemberRows does not call it): they open with their campaign. It sits at the end
  // of the title line, so it adds no height to the row (beside the state it stacked under it and grew a lone row).
  const renderOpenBtn = (row: FeedRow) => (
    <button
      type="button"
      className="tf-open-btn"
      aria-label={`Open ${row.title}`}
      title="Open"
      onClick={(e) => {
        e.stopPropagation();
        onOpenCampaign?.(row.id);
      }}
    >
      <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden />
    </button>
  );

  /** An incident of an open campaign: a row of its own that opens, one level in, to the same panel a lone incident has. */
  const renderMemberRows = (row: FeedRow) =>
    row.members.map((member) => {
      const memberId = member.threat.event._id;
      const key = `${row.id}::${memberId}`;
      const open = expanded.has(key);
      const Chevron = open ? ChevronDown : ChevronRight;
      const isNew =
        member.firstEventMs != null &&
        nowMs - member.firstEventMs < 60 * 60 * 1000 &&
        nowMs >= member.firstEventMs;
      return (
        <div key={memberId} className="tf-table-member">
          <div
            className="tf-table-row tf-table-row-child"
            data-sev={member.severity}
            onClick={() => {
              // Selecting the text of a row with the mouse is not a request to expand it.
              if (window.getSelection?.()?.toString()) return;
              toggleExpand(key);
            }}
          >
            <div className="tf-col-sev">
              <span className={`tf-sev ${SEVERITY_CLASS[member.severity]}`}>{member.severity}</span>
            </div>
            <div className="tf-col-incident">
              <div className="tf-incident tf-incident-child">
                <div className="tf-incident-top">
                  <button
                    type="button"
                    className="tf-chevron"
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${member.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(key);
                    }}
                  >
                    <Chevron className="w-3.5 h-3.5" aria-hidden />
                  </button>
                  <div className="tf-incident-copy">
                    <div className="tf-incident-title-row">
                      <span className="tf-incident-title" title={member.title}>{member.title}</span>
                      {isNew ? <span className="tf-new-badge">new</span> : null}
                    </div>
                    <div className="tf-incident-meta">
                      <span className="tf-incident-id is-threat">{incidentLabel(memberId)}</span>
                      {member.description ? (
                        <span className="tf-incident-desc" title={member.description}>{member.description}</span>
                      ) : (
                        <span className="tf-incident-desc is-empty">No description</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="tf-col-age">{renderUpdated(member.lastUpdatedMs)}</div>
            <div className="tf-col-sources">
              <SourceNames sources={member.sources} />
            </div>
            <div className="tf-col-entities">{renderEntityChips(member.entities)}</div>
            <div className="tf-col-state">
              <div className="tf-state-stack">{renderState(member.state)}</div>
            </div>
          </div>
          {open
            ? renderDetail(member, [member], member.title, renderActions(memberId, scopeOfThreat(member.threat)), 2)
            : null}
        </div>
      );
    });

  return (
    <section id="c3-threat-feed" className="c3-band">
      <h2>
        Threat Feed
        <span className="meta">Click a row to expand it</span>
      </h2>
      <div className="flex flex-wrap gap-2 mb-3">
        {(['active', 'suppressed'] as FeedView[]).map((item) => (
          <button
            key={item}
            type="button"
            className={`c3-pill${view === item ? ' active' : ''}`}
            onClick={() => setView(item)}
          >
            {item === 'active' ? 'Active' : 'Resolved'}
          </button>
        ))}
        <div className="c3-feed-search">
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            aria-label="Search the threat feed"
            placeholder="Search the feed"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <FeedFilterControls
          sources={sources}
          value={{
            sources: sourceFilter,
            severities: severityFilter,
            states: stateFilter,
            types: typeFilter,
            actions: actionFilter,
            time: timeFilter,
          }}
          onChange={(patch) => {
            if (patch.sources) setSourceFilter(patch.sources);
            if (patch.severities) setSeverityFilter(patch.severities);
            if (patch.states) setStateFilter(patch.states);
            if (patch.types) setTypeFilter(patch.types);
            if (patch.actions) setActionFilter(patch.actions);
            if (patch.time) setTimeFilter(patch.time);
          }}
        />
        <button type="button" className="c3-pill" onClick={() => refresh()}>
          <span className="inline-flex items-center gap-2">
            <RefreshCw size={16} strokeWidth={1.5} aria-hidden />
            Refresh
          </span>
        </button>
        {filtering && view === 'active' ? (
          <span className="self-center text-[13px] text-c3-mute">
            Showing {visibleRows.length} of {rows.length}{' '}
            <button type="button" className="c3-btn c3-btn-text c3-btn-sm" onClick={clearFilters}>
              Clear filters
            </button>
          </span>
        ) : null}
        {clock.asOf != null &&
          (() => {
            const asOf = (extra: string) => (
              <span className={`${extra} self-center text-[13px] text-c3-mute`.trim()}>
                As of <span className="font-mono tabular-nums">{formatUtcTime(clock.asOf, false)}</span>
              </span>
            );
            // Live and the time it is as of stay together, on the right, whichever line they fall on.
            return !clock.paused && clock.liveIntervalMs > 0 ? (
              <span className="ml-auto inline-flex items-center gap-3 self-center">
                <span
                  className="tf-live text-[13px]"
                  title={`New alerts arrive by themselves: the feed is read again every ${clock.liveIntervalMs / 1000} s`}
                >
                  <i className="tf-live-dot" aria-hidden />
                  Live
                </span>
                {asOf('')}
              </span>
            ) : (
              asOf('ml-auto')
            );
          })()}
      </div>
      {error && (
        <ErrorState title="Could not load the threat feed" onRetry={() => refresh()}>
          Nothing was changed. Try again in a moment.
        </ErrorState>
      )}

      {view === 'suppressed' ? (
        <div className="space-y-2">
          {loading && suppressed.length === 0 && <Skeleton lines={3} label="Loading resolved alerts…" />}
          {!loading && !error && suppressed.length === 0 && (
            <EmptyState title="No resolved alerts">
              Alerts the system judges to be false positives are kept here, so you can review them.
            </EmptyState>
          )}
          {suppressed.map((candidate) => (
            <div key={candidate._id} className="bg-c3-tile border border-c3-tile-bd rounded-xl px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium">{sourceLabel(candidate.source) || 'Unknown source'}</div>
                  <div className="text-[12px] text-c3-mute">
                    Resolved as a false positive · {formatUtcDateTime(candidate.detected_at)}
                  </div>
                </div>
                <button
                  type="button"
                  disabled
                  title="Reopening a resolved alert is not available yet"
                  className="text-[12px] text-c3-mute border border-c3-tile-bd rounded px-2 py-1 opacity-60"
                >
                  Reopen
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : error ? null : (
        <div className="tf-table-wrap">
          {sortKey !== 'default' ? (
          <div className="tf-sort-bar">
            <button
              type="button"
              className="tf-default-sort"
              onClick={() => {
                setSortKey('default');
                setSortDir('desc');
              }}
              title="Severity ↓ · Last updated newest first · Campaign first"
            >
              Default sorting
            </button>
          </div>
          ) : null}
          <div className="tf-table">
          <div className="tf-table-head">
            <SortHead label="Severity" col="severity" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
            <SortHead
              label="Campaign / Incident"
              col="incident"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSortColumn}
            />
            <SortHead
              label="Last updated"
              hint="Time since the latest activity"
              col="updated"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSortColumn}
            />
            <SortHead label="Sources" col="sources" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
            <SortHead
              label="Affected entities"
              col="entities"
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSortColumn}
            />
            <SortHead label="State" col="state" sortKey={sortKey} sortDir={sortDir} onSort={onSortColumn} />
          </div>
          {loading && rows.length === 0 ? (
            <div className="px-3 py-4">
              <Skeleton lines={5} label="Loading the threat feed…" />
            </div>
          ) : visibleRows.length === 0 ? (
            rows.length > 0 ? (
              <EmptyState
                title="No incidents match these filters"
                action={
                  <button
                    type="button"
                    className="c3-pill"
                    onClick={() => {
                      clearFilters();
                    }}
                  >
                    Clear filters
                  </button>
                }
              >
                Clear a filter to see more.
              </EmptyState>
            ) : (
              <EmptyState title="No incidents yet">When the system detects a threat, it appears here.</EmptyState>
            )
          ) : (
            visibleRows.map((row) => {
              const open = expanded.has(row.id);
              return (
                <div
                  key={row.id}
                  className={`tf-table-group${open ? ' is-open' : ''}${row.kind === 'campaign' ? ' is-campaign' : ''}${fresh.has(row.id) ? ' is-fresh' : ''}`}
                >
                  {/* A click anywhere on the row expands it; the chevron is the control a keyboard and a screen reader
                      reach, so the Open and View in Graph buttons are not nested inside another button. */}
                  <div
                    className="tf-table-row"
                    data-sev={row.severity}
                    onClick={() => {
                      // Selecting the text of a row with the mouse is not a request to expand it.
                      if (window.getSelection?.()?.toString()) return;
                      toggleExpand(row.id);
                    }}
                  >
                    <div className="tf-col-sev">
                      <span className={`tf-sev ${SEVERITY_CLASS[row.severity]}`}>{row.severity}</span>
                    </div>
                    <div className="tf-col-incident">{renderIncidentCell(row, open)}</div>
                    <div className="tf-col-age">{renderUpdated(row.lastUpdatedMs)}</div>
                    <div className="tf-col-sources" title={sourcesLine(row.sources)}>
                      <SourceNames sources={row.sources} />
                    </div>
                    <div className="tf-col-entities">{renderEntityChips(row.entities)}</div>
                    <div className="tf-col-state">
                      <div className="tf-state-stack">{renderState(row.state)}</div>
                    </div>
                  </div>
                  {open && row.kind === 'campaign' ? (
                    <div className="tf-table-children">
                      {renderDetail(row, row.members, row.title, renderActions(row.id, scopeOfRow(row)), 1)}
                      <div className="tf-members-head">
                        Incidents in this campaign <span>{row.members.length}</span>
                      </div>
                      {renderMemberRows(row)}
                    </div>
                  ) : null}
                  {open && row.kind === 'standalone' ? (
                    <div className="tf-table-children is-single">
                      {renderDetail(row, row.members, row.title, renderActions(row.id, scopeOfRow(row)), 1)}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
          </div>
        </div>
      )}
    </section>
  );
};

export default ThreatFeed;
